import React, { useEffect, useMemo, useRef } from 'react';
import './AgeWheel.css';

export const AGE_WHEEL_MIN = 18;
export const AGE_WHEEL_MAX = 120;
export const AGE_WHEEL_DEFAULT = 22;
const AGE_WHEEL_ITEM_HEIGHT = 30;
const AGE_WHEEL_VISIBLE_DEFAULT = 5;

/**
 * iOS-style vertical age picker (same as Fox chat). Item height must match `.age-wheel-item` in CSS.
 * @param {number} value — centered age (18–120)
 * @param {(n: number) => void} onChange
 * @param {boolean} [interactionLocked]
 * @param {string|null} [title] — heading above wheel; default `YOUR AGE`; pass `null` to hide (use outer label)
 * @param {number} [visibleRowCount] — how many age rows fit in the mask (default 5 — Fox chat). Use 3 in tight popups.
 */
export default function AgeWheel({
  value,
  onChange,
  interactionLocked = false,
  title = 'YOUR AGE',
  visibleRowCount = AGE_WHEEL_VISIBLE_DEFAULT,
}) {
  const containerRef = useRef(null);
  const initRef = useRef(false);
  const scrollEndTimerRef = useRef(null);
  const ages = useMemo(() => {
    const arr = [];
    for (let a = AGE_WHEEL_MIN; a <= AGE_WHEEL_MAX; a += 1) arr.push(a);
    return arr;
  }, []);
  const rows = Number.isFinite(visibleRowCount) && visibleRowCount >= 3
    ? Math.min(15, Math.round(visibleRowCount))
    : AGE_WHEEL_VISIBLE_DEFAULT;
  const padRows = Math.floor(rows / 2);
  const maskHeightPx = rows * AGE_WHEEL_ITEM_HEIGHT;

  useEffect(() => {
    const el = containerRef.current;
    if (!el || initRef.current) return;
    initRef.current = true;
    const initial = typeof value === 'number' ? value : AGE_WHEEL_DEFAULT;
    const idx = Math.max(0, Math.min(ages.length - 1, initial - AGE_WHEEL_MIN));
    el.scrollTop = idx * AGE_WHEEL_ITEM_HEIGHT;
  }, [value, ages.length]);

  const handleScroll = () => {
    if (interactionLocked) return;
    const el = containerRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollTop / AGE_WHEEL_ITEM_HEIGHT);
    const clamped = Math.max(0, Math.min(ages.length - 1, idx));
    const next = ages[clamped];
    if (next !== value) onChange(next);
    if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
    scrollEndTimerRef.current = setTimeout(() => {
      const target = clamped * AGE_WHEEL_ITEM_HEIGHT;
      if (Math.abs(el.scrollTop - target) > 1) {
        el.scrollTo({ top: target, behavior: 'smooth' });
      }
    }, 120);
  };

  return (
    <div
      className={`age-wheel${interactionLocked ? ' age-wheel--interaction-locked' : ''}`}
      aria-label="Pick your age"
      aria-disabled={interactionLocked}
    >
      {title != null && title !== '' ? (
        <h4 className="age-wheel-title">{title}</h4>
      ) : null}
      <div className="age-wheel-mask" style={{ height: maskHeightPx }}>
        <div
          ref={containerRef}
          className="age-wheel-scroll"
          onScroll={handleScroll}
          role="listbox"
          aria-activedescendant={`age-wheel-item-${value}`}
          tabIndex={interactionLocked ? -1 : 0}
        >
          <div
            className="age-wheel-pad"
            style={{ height: padRows * AGE_WHEEL_ITEM_HEIGHT }}
            aria-hidden
          />
          {ages.map((a) => (
            <button
              key={a}
              id={`age-wheel-item-${a}`}
              type="button"
              className={`age-wheel-item${a === value ? ' age-wheel-item--selected' : ''}`}
              style={{ height: AGE_WHEEL_ITEM_HEIGHT }}
              onClick={() => {
                if (interactionLocked) return;
                const el = containerRef.current;
                if (el) {
                  el.scrollTo({
                    top: (a - AGE_WHEEL_MIN) * AGE_WHEEL_ITEM_HEIGHT,
                    behavior: 'smooth',
                  });
                }
                onChange(a);
              }}
              role="option"
              aria-selected={a === value}
            >
              {a}
            </button>
          ))}
          <div
            className="age-wheel-pad"
            style={{ height: padRows * AGE_WHEEL_ITEM_HEIGHT }}
            aria-hidden
          />
        </div>
        <div className="age-wheel-highlight" aria-hidden />
      </div>
    </div>
  );
}
